import React, { Component, Fragment } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Tooltip,
  Modal,
  notification,
  Breadcrumb,
  Spin,
  Alert,
} from "antd";
import {
  SyncOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  fetchCronJobsListAPI,
  triggerCronJobNowAPI,
  cleanupCronHistoryAPI,
} from "../../Graphs/Report/cronJobsAPI";
import CronJobRunsModal from "./CronJobRunsModal";
import "./CronJobs.scss";

export function formatCronSchedule(cronTime) {
  if (!cronTime) return "Manual only";
  const fields = cronTime.trim().split(/\s+/);
  const [minute, hour, day, month, weekday] = fields;
  if (fields.length !== 5 || !/^\d+$/.test(minute) || !/^\d+$/.test(hour) ||
      Number(minute) > 59 || Number(hour) > 23 || month !== "*" || weekday !== "*") {
    return "Custom schedule";
  }
  const hourNumber = Number(hour);
  const time = `${hourNumber % 12 || 12}:${String(Number(minute)).padStart(2, "0")} ${hourNumber < 12 ? "AM" : "PM"}`;
  if (day === "*") return `Daily at ${time}`;
  if (/^\d+$/.test(day) && Number(day) >= 1 && Number(day) <= 31) {
    return `Monthly on day ${Number(day)} at ${time}`;
  }
  return "Custom schedule";
}

export class CronJobsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      jobs: [],
      isLoading: false,
      runningJobIds: new Set(),
      selectedJobForModal: null,
      isRunsModalOpen: false,
      isCleaningUp: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadJobs();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (state, callback) => {
    if (this._isMounted) {
      this.setState(state, callback);
    }
  };

  loadJobs = async () => {
    try {
      this.safeSetState({ isLoading: true });
      const { authToken } = this.props;
      const res = await fetchCronJobsListAPI(authToken);

      if (res && res.status === 200 && Array.isArray(res.data)) {
        this.safeSetState({ jobs: res.data });
      } else {
        this.safeSetState({ jobs: [] });
      }
    } catch (err) {
      console.error("[CronJobsList] loadJobs error:", err);
      notification.error({
        message: "Failed to load cron jobs",
        description: err.message || "Network error",
      });
    } finally {
      this.safeSetState({ isLoading: false });
    }
  };

  handleRunNow = (job) => {
    const { authToken } = this.props;
    Modal.confirm({
      title: `Execute "${job.name}" Immediately?`,
      icon: <ExclamationCircleOutlined />,
      content:
        "This will trigger the cron job's execution pipeline immediately on the backend server. Do you want to proceed?",
      okText: "Run Now",
      cancelText: "Cancel",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        const { runningJobIds } = this.state;
        const newSet = new Set(runningJobIds);
        newSet.add(job.id);
        this.safeSetState({ runningJobIds: newSet });

        try {
          const res = await triggerCronJobNowAPI(authToken, job.id);
          if (res && res.status === 200) {
            notification.success({
              message: "Job Executed Successfully",
              description: `"${job.name}" completed in ${res.data?.durationMs || 0}ms.`,
            });
          } else {
            notification.error({
              message: "Job Execution Failed",
              description:
                res?.message || res?.error || "Job execution encountered an error.",
            });
          }
        } catch (err) {
          notification.error({
            message: "Execution Request Error",
            description: err.message || "Failed to trigger job.",
          });
        } finally {
          const updatedSet = new Set(this.state.runningJobIds);
          updatedSet.delete(job.id);
          this.safeSetState({ runningJobIds: updatedSet });
          this.loadJobs();
        }
      },
    });
  };

  handleManualCleanup = () => {
    const { authToken } = this.props;
    Modal.confirm({
      className: "cron-cleanup-confirm",
      title: "Delete old history?",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      content:
        "Delete history older than 30 days. History from the last 30 days will stay. This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          this.safeSetState({ isCleaningUp: true });
          const res = await cleanupCronHistoryAPI(authToken, 30);
          if (res && res.status === 200) {
            notification.success({
              message: "Old history deleted",
              description: "History older than 30 days was deleted.",
            });
            this.loadJobs();
          } else {
            notification.error({
              message: "Could not delete history",
              description: res?.message || "Please try again.",
            });
          }
        } catch (err) {
          notification.error({
            message: "Could not delete history",
            description: err.message || "Please try again.",
          });
        } finally {
          this.safeSetState({ isCleaningUp: false });
        }
      },
    });
  };

  openRunsModal = (job) => {
    this.safeSetState({
      selectedJobForModal: job,
      isRunsModalOpen: true,
    });
  };

  closeRunsModal = () => {
    this.safeSetState({
      selectedJobForModal: null,
      isRunsModalOpen: false,
    });
    this.loadJobs();
  };

  formatDuration = (durationMs) => {
    if (durationMs === null || durationMs === undefined) return "-";
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(2)}s`;
  };

  getStatusTag = (status, isRunning) => {
    if (isRunning || status === "running") {
      return (
        <Tag color="processing" icon={<SyncOutlined spin />}>
          RUNNING
        </Tag>
      );
    }

    switch (status) {
      case "completed":
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            COMPLETED
          </Tag>
        );
      case "failed":
        return (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            FAILED
          </Tag>
        );
      case "partial":
        return (
          <Tag color="warning" icon={<ClockCircleOutlined />}>
            PARTIAL
          </Tag>
        );
      default:
        return <Tag color="default">NEVER RUN</Tag>;
    }
  };

  render() {
    const {
      jobs,
      isLoading,
      runningJobIds,
      selectedJobForModal,
      isRunsModalOpen,
      isCleaningUp,
    } = this.state;
    const { authToken } = this.props;

    const totalJobs = jobs.length;
    const healthyJobs = jobs.filter(
      (j) => !j.lastRun || j.lastRun.status === "completed"
    ).length;
    const failedLastRuns = jobs.filter(
      (j) => j.lastRun && j.lastRun.status === "failed"
    ).length;
    const totalRuns30d = jobs.reduce(
      (acc, j) => acc + ((j.stats && j.stats.totalRuns30d) || 0),
      0
    );

    const columns = [
      {
        title: "Cron Job",
        key: "job",
        width: "25%",
        render: (_, record) => (
          <div className="job-title-cell">
            <Tooltip title={record.description}>
              <Button
                type="link"
                className="job-name"
                onClick={() => this.openRunsModal(record)}
              >
                {record.name}
              </Button>
            </Tooltip>
            <div className="job-desc">{record.id}</div>
          </div>
        ),
      },
      {
        title: "Schedule",
        key: "schedule",
        width: "17%",
        render: (_, record) => (
          <div className="schedule-cell">
            <Tooltip title={record.cronTime}>
              <span>{formatCronSchedule(record.cronTime)}</span>
            </Tooltip>
          </div>
        ),
      },
      {
        title: "Last Run",
        key: "lastRun",
        width: "16%",
        render: (_, record) => {
          const lr = record.lastRun;
          if (!lr || !lr.startedAt) {
            return <span style={{ color: "#bfbfbf" }}>Never executed</span>;
          }
          return (
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>
                <div>{moment(lr.startedAt).format("YYYY-MM-DD")}</div>
                <div>{moment(lr.startedAt).format("HH:mm:ss")}</div>
              </div>
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                {moment(lr.startedAt).fromNow()}
              </div>
              {lr.triggerType === "manual" && (
                <Tag color="purple" style={{ marginTop: 2, fontSize: 10 }}>
                  Manual ({lr.triggeredBy})
                </Tag>
              )}
            </div>
          );
        },
      },
      {
        title: "Duration",
        key: "duration",
        width: "8%",
        render: (_, record) => {
          const lr = record.lastRun;
          return (
            <span className="duration-badge">
              {lr ? this.formatDuration(lr.durationMs) : "-"}
            </span>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: "12%",
        render: (_, record) => {
          const isJobActive =
            record.isRunning || runningJobIds.has(record.id);
          const status = record.lastRun ? record.lastRun.status : null;
          return this.getStatusTag(status, isJobActive);
        },
      },
      {
        title: "30-Day History",
        key: "stats",
        width: "12%",
        render: (_, record) => {
          const stats = record.stats || {};
          return (
            <div style={{ fontSize: 12 }}>
              <div>
                Total: <strong>{stats.totalRuns30d || 0}</strong>
              </div>
              {stats.failedRuns30d > 0 ? (
                <div style={{ color: "#cf1322", fontWeight: 500 }}>
                  Failed: {stats.failedRuns30d}
                </div>
              ) : (
                <div style={{ color: "#389e0d" }}>Zero Failures</div>
              )}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: "10%",
        render: (_, record) => {
          const isJobActive =
            record.isRunning || runningJobIds.has(record.id);
          return (
            <div className="cron-row-actions">
              <Tooltip placement="bottom" title="Run now">
                <span>
                  <Button
                    shape="circle"
                    aria-label="Run now"
                    icon={<PlayCircleOutlined />}
                    loading={isJobActive}
                    disabled={isJobActive}
                    onClick={() => this.handleRunNow(record)}
                  />
                </span>
              </Tooltip>
              <Tooltip placement="bottom" title="History">
                <Button
                  shape="circle"
                  aria-label="History"
                  icon={<HistoryOutlined />}
                  onClick={() => this.openRunsModal(record)}
                />
              </Tooltip>
            </div>
          );
        },
      },
    ];

    return (
      <Fragment>
        <div className="cron-jobs-page">
          <div className="cron-header-card">
            <div className="cron-header-content">
              <div className="title-section">
                <h3>Report Schedule & Background Tasks</h3>
              </div>
              <div className="header-actions">
                <Button
                  icon={<SyncOutlined />}
                  loading={isLoading}
                  onClick={this.loadJobs}
                >
                  Refresh
                </Button>
                <Button
                  danger
                  className="cron-delete-history-btn"
                  icon={<DeleteOutlined />}
                  loading={isCleaningUp}
                  onClick={this.handleManualCleanup}
                >
                  Delete old history
                </Button>
              </div>
            </div>
          </div>

          <div className="kpi-row">
            <div className="kpi-card">
              <div className="kpi-icon-wrap primary">
                <ClockCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Registered Jobs</div>
                <div className="kpi-value">{totalJobs}</div>
                <div className="kpi-sub">Active in Cron Registry</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap success">
                <CheckCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Healthy / Completed</div>
                <div className="kpi-value">{healthyJobs}</div>
                <div className="kpi-sub">Last run succeeded</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap danger">
                <CloseCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Failed Last Runs</div>
                <div className="kpi-value">{failedLastRuns}</div>
                <div className="kpi-sub">Require investigation</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap info">
                <DatabaseOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">30-Day Total Runs</div>
                <div className="kpi-value">{totalRuns30d}</div>
                <div className="kpi-sub">Retained instances</div>
              </div>
            </div>
          </div>

          <Card
            className="cron-table-card"
            bordered={false}
          >
            <Table
              className="cron-jobs-table"
              dataSource={jobs}
              columns={columns}
              rowKey="id"
              loading={isLoading}
              pagination={false}
              tableLayout="fixed"
            />
          </Card>

          {selectedJobForModal && (
            <CronJobRunsModal
              visible={isRunsModalOpen}
              job={selectedJobForModal}
              authToken={authToken}
              onClose={this.closeRunsModal}
              onJobExecuted={this.loadJobs}
            />
          )}
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken: state.auth?.authToken || state.auth?.admin_token || state.auth?.token || "",
});

export default connect(mapStateToProps)(withRouter(CronJobsList));
